import React, { useState, useEffect } from 'react';

const Navbar = ({ onOpenModal }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when clicking a link
  const handleLinkClick = () => {
    setIsOpen(false);
  };

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${
      scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg py-2' : 'bg-white shadow-lg py-0'
    }`}>
      <div className="container-custom">
        <div className="flex justify-between items-center h-16 sm:h-20">
          
          {/* Logo Section - Responsive */}
          <div className="flex-shrink-0 flex items-center gap-2 sm:gap-3">
            <img 
              src="/logo.jpg" 
              alt="PropWhiz Logo" 
              className="h-8 sm:h-10 md:h-12 w-auto object-contain transition-all duration-300"
            />
            
            {/* Logo Text - Hidden on smallest screens, visible on sm and up */}
            <div className="hidden xs:block sm:hidden md:block">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-propwhiz-blue leading-none">
                PropWhiz
              </h1>
              <p className="text-[10px] sm:text-xs text-gray-600 hidden xs:block">
                Real Estate Solutions
              </p>
            </div>
          </div>

          {/* Desktop Menu - Hidden on tablet and below */}
          <div className="hidden lg:flex space-x-6 xl:space-x-8">
            <a href="#home" className="text-gray-700 hover:text-propwhiz-blue font-medium transition relative group">
              Home
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-propwhiz-blue transition-all group-hover:w-full"></span>
            </a>
            <a href="#how-it-works" className="text-gray-700 hover:text-propwhiz-blue font-medium transition relative group">
              How It Works
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-propwhiz-blue transition-all group-hover:w-full"></span>
            </a>
            <a href="#about" className="text-gray-700 hover:text-propwhiz-blue font-medium transition relative group">
              About
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-propwhiz-blue transition-all group-hover:w-full"></span>
            </a>
            <a href="#locations" className="text-gray-700 hover:text-propwhiz-blue font-medium transition relative group">
              Locations
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-propwhiz-blue transition-all group-hover:w-full"></span>
            </a>
            <a href="#contact" className="text-gray-700 hover:text-propwhiz-blue font-medium transition relative group">
              Contact
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-propwhiz-blue transition-all group-hover:w-full"></span>
            </a>
          </div>

          {/* Phone & CTA - Responsive */}
          <div className="hidden md:flex items-center space-x-3 lg:space-x-4">
            <a 
              href="tel:9722553567" 
              className="text-propwhiz-blue font-semibold text-sm lg:text-base hover:text-propwhiz-accent transition flex items-center gap-1"
            >
              <svg className="w-4 h-4 lg:w-5 lg:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              (972) 255-3567
            </a>
            <button 
              onClick={onOpenModal} 
              className="btn-primary text-sm lg:text-base px-4 lg:px-6 py-2 lg:py-3 whitespace-nowrap"
            >
              Get Cash Offer
            </button>
          </div>

          {/* Mobile Menu Button - Visible on tablet and below */}
          <div className="flex md:hidden items-center gap-2">
            {/* Mobile Phone Icon */}
            <a 
              href="tel:9722553567" 
              className="text-propwhiz-blue p-2 hover:bg-gray-100 rounded-full transition"
              aria-label="Call us"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </a>
            
            {/* Menu Toggle Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-700 hover:text-propwhiz-blue focus:outline-none p-2 hover:bg-gray-100 rounded-lg transition"
              aria-label="Toggle menu"
            >
              <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu - Slide Down Animation */}
        <div 
          className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="pb-4 pt-2 space-y-1">
            <a 
              href="#home" 
              onClick={handleLinkClick}
              className="block py-3 px-4 text-gray-700 hover:text-propwhiz-blue hover:bg-gray-50 rounded-lg font-medium transition"
            >
              Home
            </a>
            <a 
              href="#how-it-works" 
              onClick={handleLinkClick}
              className="block py-3 px-4 text-gray-700 hover:text-propwhiz-blue hover:bg-gray-50 rounded-lg font-medium transition"
            >
              How It Works
            </a>
            <a 
              href="#about" 
              onClick={handleLinkClick}
              className="block py-3 px-4 text-gray-700 hover:text-propwhiz-blue hover:bg-gray-50 rounded-lg font-medium transition"
            >
              About
            </a>
            <a 
              href="#locations" 
              onClick={handleLinkClick}
              className="block py-3 px-4 text-gray-700 hover:text-propwhiz-blue hover:bg-gray-50 rounded-lg font-medium transition"
            >
              Locations
            </a>
            <a 
              href="#contact" 
              onClick={handleLinkClick}
              className="block py-3 px-4 text-gray-700 hover:text-propwhiz-blue hover:bg-gray-50 rounded-lg font-medium transition"
            >
              Contact
            </a>
            
            {/* Mobile CTA Section */}
            <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
              <a 
                href="tel:9722553567" 
                className="flex items-center justify-center gap-2 text-propwhiz-blue font-semibold py-3 px-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition"
                onClick={handleLinkClick}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                (972) 255-3567
              </a>
              
              <button 
                onClick={() => { 
                  onOpenModal(); 
                  handleLinkClick();
                }} 
                className="w-full btn-primary py-3 flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Get Cash Offer
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Add this to your global CSS or in a style tag */}
      <style jsx>{`
        @media (min-width: 480px) {
          .xs\\:block {
            display: block;
          }
        }
      `}</style>
    </nav>
  );
};

export default Navbar;